(() => {
  'use strict';

  if (window.BrandCart || !window.BrandStore) return;

  const store = window.BrandStore;
  const initialDisabled = new WeakMap();
  const boundDialogs = new WeakSet();
  let busy = false;
  let stale = false;
  let renderVersion = 0;
  let returnFocus = null;

  const roots = () => Array.from(document.querySelectorAll('[data-brand-cart-root]'));
  const drawer = () => document.querySelector('[data-brand-cart-dialog]');
  const cartUrl = () => new URL('cart', new URL(store.config.routesRoot, window.location.origin)).href;
  const activeRoot = () => {
    const modal = drawer();
    return (modal?.open && modal.closest('[data-brand-cart-root]')) || document.querySelector('[data-brand-cart-surface="page"]') || roots()[0];
  };

  function message(key, root = activeRoot()) {
    return root?.querySelector('[data-brand-cart-content]')?.dataset[key] || '';
  }

  function announce(value, root = activeRoot()) {
    const status = root?.querySelector('[data-brand-cart-status]');
    if (status) status.textContent = value;
  }

  function clearErrors() {
    for (const root of roots()) {
      const error = root.querySelector('[data-brand-cart-error]');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
      const recovery = root.querySelector('[data-brand-cart-recovery]');
      if (recovery) recovery.hidden = true;
      const fallback = root.querySelector('[data-brand-checkout-fallback]');
      if (fallback) fallback.hidden = true;
    }
  }

  function showError(value, root = activeRoot(), recovery = false) {
    const error = root?.querySelector('[data-brand-cart-error]');
    if (error) {
      error.textContent = value;
      error.hidden = false;
    }
    const link = root?.querySelector('[data-brand-cart-recovery]');
    if (link) link.hidden = !recovery;
    announce('', root);
  }

  function updateLocks() {
    for (const root of roots()) {
      root.setAttribute('aria-busy', busy ? 'true' : 'false');
      for (const control of root.querySelectorAll('[data-brand-cart-control]')) {
        if (!initialDisabled.has(control)) initialDisabled.set(control, control.disabled);
        control.disabled = initialDisabled.get(control) || busy || stale;
      }
      for (const link of root.querySelectorAll('[data-brand-cart-remove]')) {
        if (busy || stale) link.setAttribute('aria-disabled', 'true');
        else link.removeAttribute('aria-disabled');
      }
    }
  }

  function focusSnapshot() {
    const element = document.activeElement;
    const root = element?.closest('[data-brand-cart-root]');
    const line = element?.closest('[data-brand-cart-line]');
    if (!root) return null;
    let selector = '';
    if (element.matches('[data-brand-cart-quantity]')) selector = '[data-brand-cart-quantity]';
    else if (element.matches('[data-brand-cart-step]')) selector = `[data-brand-cart-step="${element.dataset.brandCartStep}"]`;
    else if (element.matches('[data-brand-cart-remove]')) selector = '[data-brand-cart-remove]';
    else if (element.matches('[data-brand-cleaner-add]')) selector = '[data-brand-cleaner-add]';
    if (!selector) return null;
    return { element, root, key: line?.dataset.lineKey, selector };
  }

  function restoreFocus(snapshot) {
    if (!snapshot) return;
    const current = document.activeElement;
    if (current && current !== document.body && current !== snapshot.element && current.isConnected) return;
    let scope = snapshot.root;
    if (snapshot.key) scope = Array.from(scope.querySelectorAll('[data-brand-cart-line]')).find(line => line.dataset.lineKey === snapshot.key);
    const target = scope?.querySelector(snapshot.selector);
    if (target && !target.disabled) {
      target.focus({ preventScroll: true });
      return;
    }
    const fallback = snapshot.root.querySelector('[data-brand-cart-quantity]:not(:disabled), [data-brand-cart-close], [data-brand-cart-checkout]:not(:disabled), .brand-cart-empty a');
    fallback?.focus({ preventScroll: true });
  }

  function sectionIds() {
    const ids = roots().map(root => root.dataset.sectionId).filter(Boolean);
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return [...new Set(ids)].slice(0, 5);
  }

  function sectionPayload() {
    return { sections: sectionIds(), sections_url: window.location.pathname };
  }

  async function readSections(ids) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', ids.join(','));
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      // No "Accept: application/json" header: on product URLs Shopify would return the product JSON instead of the sections.
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart sections could not be loaded.');
      const result = await response.json();
      if (!result || typeof result !== 'object' || Array.isArray(result)) throw new Error('Cart sections were not returned.');
      return result;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function applySection(id, html) {
    if (typeof html !== 'string' || !html.trim()) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    if (id === 'cart-icon-bubble') {
      const target = document.getElementById('cart-icon-bubble');
      if (!target) return true;
      const source = parsed.querySelector('.shopify-section') || parsed.body;
      if (!source.childNodes.length) return false;
      target.replaceChildren(...source.childNodes);
      return true;
    }
    const targetRoot = roots().find(root => root.dataset.sectionId === id);
    if (!targetRoot) return true;
    const sourceRoot = Array.from(parsed.querySelectorAll('[data-brand-cart-root]')).find(root => root.dataset.sectionId === id);
    const source = sourceRoot?.querySelector('[data-brand-cart-content]');
    const target = targetRoot.querySelector('[data-brand-cart-content]');
    if (!source || !target) return false;
    const scrollTop = target.scrollTop;
    target.replaceWith(source);
    source.scrollTop = scrollTop;
    store.hydrate(source);
    return true;
  }

  async function syncSections(result) {
    const version = ++renderVersion;
    const ids = sectionIds();
    const snapshot = focusSnapshot();
    const missing = ids.filter(id => !applySection(id, result?.sections?.[id]));
    if (missing.length) {
      let sections;
      try {
        sections = await readSections(missing);
      } catch (error) {
        if (missing.some(id => id !== 'cart-icon-bubble')) throw error;
        sections = {};
      }
      if (version !== renderVersion) return false;
      const failed = missing.filter(id => !applySection(id, sections[id]));
      if (failed.some(id => id !== 'cart-icon-bubble')) throw new Error('A cart section could not be rendered.');
    }
    if (version !== renderVersion) return false;
    stale = false;
    updateLocks();
    if (!busy) restoreFocus(snapshot);
    document.dispatchEvent(new CustomEvent('brand:cart-updated', { detail: { cart: result || null } }));
    return true;
  }

  async function runMutation(operation, root, adding = false) {
    if (busy || stale) return false;
    const snapshot = focusSnapshot();
    clearErrors();
    busy = true;
    updateLocks();
    announce(message('loading', root), root);
    const addButton = root.querySelector('[data-brand-cleaner-add]');
    const originalAddText = addButton?.textContent;
    if (adding && addButton) addButton.textContent = message('cleanerAdding', root);
    try {
      return await store.enqueue(async () => {
        let result;
        try {
          result = await operation(sectionPayload());
        } catch (error) {
          try {
            await syncSections();
          } catch (renderError) {
            stale = true;
          }
          showError(message(stale ? 'renderError' : 'error', root), root, true);
          return false;
        }
        try {
          await syncSections(result);
        } catch (error) {
          stale = true;
          showError(message('renderError', root), root, true);
          return false;
        }
        announce(message('updated', root), root);
        return true;
      });
    } finally {
      busy = false;
      updateLocks();
      if (adding && addButton?.isConnected) addButton.textContent = originalAddText;
      restoreFocus(snapshot);
    }
  }

  function validQuantity(input, quantity) {
    const min = Number(input.dataset.ruleMin) || 1;
    const step = Number(input.dataset.ruleStep) || 1;
    const max = input.hasAttribute('max') ? Number(input.max) : Number.POSITIVE_INFINITY;
    return Number.isSafeInteger(quantity) && quantity >= 0 && quantity <= max && (quantity === 0 || (quantity >= min && (quantity - min) % step === 0));
  }

  function changeQuantity(input, quantity) {
    const root = input.closest('[data-brand-cart-root]');
    if (!root || busy || stale || input.readOnly) return;
    if (!validQuantity(input, quantity)) {
      input.value = input.dataset.currentQuantity;
      showError(message('quantityError', root), root);
      return;
    }
    if (quantity === Number(input.dataset.currentQuantity)) return;
    const key = input.dataset.lineKey;
    input.value = String(quantity);
    void runMutation(payload => store.request('cart/change.js', { id: key, quantity, ...payload }), root);
  }

  function open(trigger) {
    const modal = drawer();
    if (!modal || typeof modal.showModal !== 'function') {
      window.location.assign(cartUrl());
      return;
    }
    if (modal.open) return;
    returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    modal.showModal();
    document.body.classList.add('brand-cart-open');
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    modal.querySelector('[data-brand-cart-close]')?.focus({ preventScroll: true });
  }

  function close() {
    const modal = drawer();
    if (modal?.open) modal.close();
  }

  function initialize() {
    document.body.classList.add('brand-cart-ready');
    const modal = drawer();
    if (modal && !boundDialogs.has(modal)) {
      boundDialogs.add(modal);
      modal.addEventListener('close', () => {
        document.body.classList.remove('brand-cart-open');
        document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
        const target = returnFocus?.isConnected && !returnFocus.disabled ? returnFocus : document.getElementById('cart-icon-bubble');
        target?.focus({ preventScroll: true });
      });
      modal.addEventListener('click', event => {
        if (event.target !== modal) return;
        const box = modal.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
      });
    }
    const headerLink = document.getElementById('cart-icon-bubble');
    if (headerLink && modal && typeof modal.showModal === 'function') {
      headerLink.setAttribute('role', 'button');
      headerLink.setAttribute('aria-haspopup', 'dialog');
      headerLink.setAttribute('aria-controls', modal.id);
      headerLink.setAttribute('aria-expanded', modal.open ? 'true' : 'false');
    }
    updateLocks();
    for (const root of roots()) store.hydrate(root);
  }

  window.BrandCart = {
    async refreshAndOpen(result, trigger) {
      try {
        const refreshed = await syncSections(result);
        open(trigger);
        return refreshed;
      } catch (error) {
        stale = true;
        open(trigger);
        updateLocks();
        showError(message('renderError'), activeRoot(), true);
        throw error;
      }
    },
    refresh: () => syncSections(),
    open,
    close
  };

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const headerLink = target.closest('#cart-icon-bubble');
    if (headerLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0 && typeof drawer()?.showModal === 'function') {
      event.preventDefault();
      open(headerLink);
      void store.enqueue(() => syncSections()).catch(() => {
        stale = true;
        updateLocks();
        showError(message('renderError'), activeRoot(), true);
      });
      return;
    }
    if (target.closest('[data-brand-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }
    const stepButton = target.closest('[data-brand-cart-step]');
    if (stepButton) {
      event.preventDefault();
      if (busy || stale || stepButton.disabled) return;
      const input = stepButton.closest('[data-brand-cart-line]')?.querySelector('[data-brand-cart-quantity]');
      if (!input) return;
      const current = Number(input.dataset.currentQuantity);
      const minimum = Number(input.dataset.ruleMin) || 1;
      const step = Number(input.dataset.ruleStep) || 1;
      const direction = Number(stepButton.dataset.brandCartStep);
      const next = direction > 0 ? current + step : current <= minimum ? 0 : Math.max(minimum, current - step);
      changeQuantity(input, next);
      return;
    }
    const remove = target.closest('[data-brand-cart-remove]');
    if (remove && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      if (busy || stale) return;
      const root = remove.closest('[data-brand-cart-root]');
      const key = remove.dataset.lineKey;
      void runMutation(payload => store.request('cart/change.js', { id: key, quantity: 0, ...payload }), root);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === ' ' && event.target instanceof Element && event.target.closest('#cart-icon-bubble') && typeof drawer()?.showModal === 'function') {
      event.preventDefault();
      event.target.closest('#cart-icon-bubble').click();
    }
  });

  document.addEventListener('change', event => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-brand-cart-quantity]')) {
      const value = event.target.value.trim();
      changeQuantity(event.target, value === '' ? Number.NaN : Number(value));
    }
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-brand-cleaner-form]')) {
      event.preventDefault();
      const root = form.closest('[data-brand-cart-root]');
      if (!root || busy || stale || event.submitter?.disabled) return;
      const id = Number(form.dataset.cleanerVariant);
      if (!Number.isSafeInteger(id) || id <= 0) return;
      await runMutation(async payload => {
        const current = await store.request('cart.js');
        if (current.items?.some(item => Number(item.variant_id) === id)) return null;
        return store.request('cart/add.js', { items: [{ id, quantity: 1 }], ...payload });
      }, root, true);
      return;
    }
    if (!form.matches('[data-brand-cart-form]')) return;
    if (event.submitter?.hasAttribute('data-brand-checkout-fallback')) return;
    if (event.submitter?.name === 'update') return;
    event.preventDefault();
    if (busy || stale || Number(form.closest('[data-brand-cart-content]')?.dataset.cartCount) === 0) return;
    clearErrors();
    busy = true;
    updateLocks();
    let success = false;
    try {
      success = await store.checkout(form);
    } catch (error) {
      const root = form.closest('[data-brand-cart-root]');
      showError(message('error', root), root, true);
      const fallback = form.querySelector('[data-brand-checkout-fallback]');
      if (fallback) fallback.hidden = false;
    } finally {
      if (!success) {
        busy = false;
        updateLocks();
      }
    }
  });

  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', () => {
    if (!drawer()?.open) document.body.classList.remove('brand-cart-open');
  });
  document.addEventListener('shopify:section:select', event => {
    if (event.target instanceof Element && event.target.querySelector('[data-brand-cart-dialog]')) open(document.getElementById('cart-icon-bubble'));
  });

  initialize();
})();
